import { create } from 'zustand';

export type MailFilter = 'all' | 'unread' | 'flagged' | 'needs_reply' | 'important';

export type View =
  | { kind: 'today' }
  | { kind: 'mail'; folder?: string; recipient?: string; domain?: string; search?: string; filter?: MailFilter }
  | { kind: 'contacts'; contactId?: string }
  | { kind: 'campaigns'; campaignId?: string; inbox?: boolean }
  | { kind: 'settings'; tab?: string };

export interface ComposerAttachment {
  id: string;
  fileName: string;
  contentType: string;
  size: number;
  base64?: string;
  /** Pending: attachment from a forwarded message still downloading. */
  loading?: boolean;
}

export interface ComposerState {
  id: string;
  mode: 'new' | 'reply' | 'replyAll' | 'forward' | 'draft';
  from?: string;
  to: string[];
  cc: string[];
  bcc: string[];
  showCc: boolean;
  subject: string;
  html: string;
  attachments: ComposerAttachment[];
  /** The message being replied to / forwarded. */
  sourceId?: string;
  draftId?: string;
  includeQuote: boolean;
  quoteHtml?: string;
  /** Instruction to run through the AI as soon as the composer opens. */
  autoAi?: string;
  /** Open with the AI prompt focused (Compose with AI). */
  aiFirst?: boolean;
  minimized: boolean;
  maximized: boolean;
  logToCrm: boolean;
  /** Editing the raw HTML source instead of the visual editor. */
  htmlMode: boolean;
  /** Wrap the message in the sending domain's brand template. */
  brand: boolean;
  /** Bumped when content is replaced programmatically so the editor reloads it. */
  rev: number;
}

export interface LocalPrefs {
  theme: 'dark' | 'light' | 'system';
  hue: number;
  density: 'cozy' | 'compact';
  conversations: boolean;
  favicons: boolean;
  notifications: boolean;
  showGists: boolean;
  remoteImages: 'ask' | 'always';
  sidebarCollapsed: boolean;
  /** Sidebar folders whose subfolders are hidden. */
  collapsedFolders: string[];
}

const LOCAL_KEY = 'dts.local-prefs';
const defaults: LocalPrefs = {
  theme: 'dark',
  hue: 275,
  density: 'cozy',
  conversations: true,
  favicons: true,
  notifications: true,
  showGists: true,
  remoteImages: 'ask',
  sidebarCollapsed: false,
  collapsedFolders: [],
};

function loadLocal(): LocalPrefs {
  try {
    return { ...defaults, ...JSON.parse(localStorage.getItem(LOCAL_KEY) ?? '{}') };
  } catch {
    return defaults;
  }
}

interface UiState {
  view: View;
  selectedId: string | null;
  selection: string[];
  composers: ComposerState[];
  /** True while two-step verification is being set up, so an update restart can't discard a scanned QR code. */
  signInBusy: boolean;
  paletteOpen: boolean;
  askOpen: boolean;
  askSeed: string | null;
  local: LocalPrefs;
  setView(v: View): void;
  select(id: string | null): void;
  toggleSelect(id: string, range?: string[]): void;
  setSelection(ids: string[]): void;
  openComposer(c: Partial<ComposerState>): string;
  updateComposer(id: string, patch: Partial<ComposerState> | ((c: ComposerState) => Partial<ComposerState>)): void;
  closeComposer(id: string): void;
  setPalette(open: boolean): void;
  openAsk(seed?: string | null): void;
  closeAsk(): void;
  setLocal(patch: Partial<LocalPrefs>): void;
  setSignInBusy(busy: boolean): void;
}

let seq = 0;

export const useUi = create<UiState>((set, get) => ({
  view: { kind: 'today' },
  selectedId: null,
  selection: [],
  composers: [],
  signInBusy: false,
  setSignInBusy: (busy) => set({ signInBusy: busy }),
  paletteOpen: false,
  askOpen: false,
  askSeed: null,
  local: loadLocal(),
  setView: (view) => set({ view, selection: [], ...(view.kind !== 'mail' ? {} : {}) }),
  select: (selectedId) => set({ selectedId }),
  toggleSelect: (id, range) =>
    set((s) => {
      if (range?.length) return { selection: Array.from(new Set([...s.selection, ...range])) };
      return { selection: s.selection.includes(id) ? s.selection.filter((x) => x !== id) : [...s.selection, id] };
    }),
  setSelection: (selection) => set({ selection }),
  openComposer: (c) => {
    const id = `c${Date.now()}-${++seq}`;
    const composer: ComposerState = {
      id,
      mode: 'new',
      to: [],
      cc: [],
      bcc: [],
      showCc: false,
      subject: '',
      html: '',
      attachments: [],
      includeQuote: true,
      minimized: false,
      maximized: false,
      logToCrm: false,
      htmlMode: false,
      brand: true,
      rev: 0,
      ...c,
    };
    // Keep at most 3 docked composers; minimize the others.
    set((s) => ({ composers: [...s.composers.map((x) => ({ ...x, minimized: true })), composer].slice(-4) }));
    return id;
  },
  updateComposer: (id, patch) =>
    set((s) => ({
      composers: s.composers.map((c) => (c.id === id ? { ...c, ...(typeof patch === 'function' ? patch(c) : patch) } : c)),
    })),
  closeComposer: (id) => set((s) => ({ composers: s.composers.filter((c) => c.id !== id) })),
  setPalette: (paletteOpen) => set({ paletteOpen }),
  openAsk: (askSeed = null) => set({ askOpen: true, askSeed }),
  closeAsk: () => set({ askOpen: false, askSeed: null }),
  setLocal: (patch) => {
    const local = { ...get().local, ...patch };
    try {
      localStorage.setItem(LOCAL_KEY, JSON.stringify(local));
    } catch {
      /* private mode */
    }
    set({ local });
  },
}));
